import posthog, { type PostHogInterface } from 'posthog-js';

import { istEigenesMessrauschen } from './posthog-eigenrauschen';
import {
  AUTOCAPTURE_URL_IGNORELIST,
  isApplicantPath,
  sanitizeEventUrls,
  sanitizeTrackedUrl,
} from './posthog-privacy';
import type { AnalyticsService } from './types';

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY ?? '';
const POSTHOG_HOST = '/ingest';

/**
 * #479: Recording-Gating für Bewerber-Pfade. Stoppt das Session-Recording
 * beim Betreten eines Bewerber-Pfads (Consent/Docs/Viewings/Discover) und
 * startet es beim Verlassen wieder. SPA-Navigationen werden via
 * History-Wrap + popstate abgefangen – `before_send` allein reicht NICHT
 * (Snapshot-Frames sind dann bereits gebuffert/gesendet).
 */
function installRecordingGate(
  ph: Pick<typeof posthog, 'startSessionRecording' | 'stopSessionRecording'>,
) {
  const sync = () => {
    if (isApplicantPath(window.location.pathname)) {
      ph.stopSessionRecording();
    } else {
      ph.startSessionRecording();
    }
  };

  sync();

  const wrap = (method: 'pushState' | 'replaceState') => {
    const original = window.history[method].bind(window.history);
    window.history[method] = ((...args: Parameters<History['pushState']>) => {
      original(...args);
      sync();
    }) as History['pushState'];
  };

  wrap('pushState');
  wrap('replaceState');
  window.addEventListener('popstate', sync);
}

/**
 * Post-init/-re-init Instanz-Setup: super-properties, First-Touch-Person-Property,
 * Recording-Gate, Initial-$pageview. Als benannte Helper gezogen (Finding B, Spec
 * 2026-07-10), damit der Re-Consent-Pfad in initialize() denselben Body ausführt
 * wie der loaded-Callback beim Erst-Init – kein Copy-Paste-Drift.
 */
function setupInstance(ph: PostHogInterface) {
  // Register super properties – sent with every event automatically
  ph.register({
    language: document.documentElement.lang || 'de',
    theme: document.documentElement.classList.contains('dark')
      ? 'dark'
      : 'light',
    viewport:
      window.innerWidth < 768
        ? 'mobile'
        : window.innerWidth < 1024
          ? 'tablet'
          : 'desktop',
    logged_in: document.cookie.indexOf('sb-') > -1 ? 'true' : 'false',
  });

  // First-Touch Landing-Path als set-once Person-Property (#421).
  // utm_*/referrer persistiert PostHog via save_campaign_params/$set_once
  // bereits selbst – hier nur der Landing-Pfad, den PostHog nicht trackt.
  ph.setPersonProperties?.(
    {},
    {
      // #479: Landing-Pfad token-bereinigt erfassen
      first_touch_landing_path: sanitizeTrackedUrl(window.location.pathname),
    },
  );

  // #479: Recording auf Bewerber-Pfaden stoppen (inkl. SPA-Navigation)
  installRecordingGate(ph);

  // #576: Initial-$pageview deterministisch HIER (capture_pageview ist aus).
  // Der loaded-Callback feuert genau einmal direkt nach init() – der einzige
  // zuverlässige Post-Consent-Trigger. Der useReportPageView-Mount-Call lief
  // (vor Consent) in den NullAnalyticsService → kein Overlap, kein Doppel.
  // super-properties sind oben bereits via ph.register() gesetzt, das
  // $pageview trägt sie. URL/utm setzt posthog-js selbst aus window.location.
  ph.capture('$pageview');

  if (process.env.NODE_ENV === 'development') {
    ph.debug();
  }
}

export const PostHogAnalyticsService: AnalyticsService = {
  initialize: async () => {
    if (typeof window === 'undefined' || !POSTHOG_KEY) return;

    // Finding B (Spec 2026-07-10): Nach Consent-Widerruf persistiert
    // opt_out_capturing() (Cookie/localStorage) + reset(). Ein erneuter Consent
    // ruft initialize() wieder auf – posthog.init() ist auf der bereits geladenen
    // Singleton aber ein No-Op (der loaded-Callback feuert NICHT erneut) → der
    // persistierte Opt-Out bliebe für immer aktiv und dieser Browser sendete nie
    // wieder Events, obwohl Consent vorliegt. Daher: explizit wieder einwilligen
    // + Instanz-Setup (inkl. Initial-$pageview aus #576) über den Helper nachholen.
    // Greift NUR bei __loaded === true → Erst-Init bleibt byte-identisch (#576).
    if (posthog.__loaded) {
      if (posthog.has_opted_out_capturing()) {
        posthog.opt_in_capturing({ captureEventName: null }); // kein $opt_in-Event
      }
      setupInstance(posthog);
      return;
    }

    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: 'identified_only',
      // #620: Nachgeladene Scripts (recorder, dead-clicks, logs, Remote-Config)
      // in den <head> statt vor das erste <body>-Script – Body-Inserts während
      // noch laufender Hydration (Returning-Consenter auf langsamen Geräten)
      // erzeugen React #418. Gleiche Einstellung wie im ExceptionTrackerProvider.
      external_scripts_inject_target: 'head',
      // #576: capture_pageview AUS. Das automatische Initial-$pageview geht bei
      // verzögertem init() (nach CookieYes-afterInteractive + Consent-Polling)
      // auf ~5s-Paid-Bounces verloren (~80% der LP-Sessions ohne Pageview trotz
      // Consent). Stattdessen: Initial-$pageview deterministisch im loaded-
      // Callback (feuert garantiert nach init), SPA-Navigationen über
      // trackPageView (useReportPageView). capture_pageleave bleibt unberührt.
      capture_pageview: false,
      capture_pageleave: true,
      // utm_*/gclid/fbclid → Event- UND First-Touch-Person-Property ($initial_*,
      // $set_once). @default true; hier explizit als Doku des Attributions-
      // Verhaltens (#421 Channel-Attribution).
      save_campaign_params: true,
      // P0.2: Exception-Autocapture explizit im Code (vorher nur Remote-Toggle
      // im Projekt) → versioniert + reviewbar.
      // P0.1: Sobald die dedizierte cookielose Exception-Instanz aktiv ist
      // (NEXT_PUBLIC_POSTHOG_EXCEPTION_TRACKER=true), fängt DIESE alle Exceptions
      // → Haupt-Instanz schaltet capture_exceptions ab, um Doppel-Capture zu
      // vermeiden. Ein einziger Flag steuert beides.
      capture_exceptions:
        process.env.NEXT_PUBLIC_POSTHOG_EXCEPTION_TRACKER !== 'true',
      // #479 (DSGVO): Recording-Masking IM CODE statt Remote-Toggle –
      // versioniert + reviewbar. maskAllInputs maskiert alle Eingabefelder,
      // maskTextSelector '*' zusätzlich ALLEN Text (Replays zeigen Struktur/
      // Klicks/Funnel, keine Inhalte – Bewerber-PII erscheint auch im
      // Vermieter-Dashboard als Text). Bei Bedarf später auf gezielte
      // Selektoren lockern.
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*',
      },
      // #479: Kein Autocapture auf Bewerber-Pfaden (Element-Texte/hrefs
      // könnten PII tragen).
      autocapture: {
        url_ignorelist: AUTOCAPTURE_URL_IGNORELIST,
      },
      // P1.2: Framework-Rauschen gar nicht erst ingesten. `redirect()` /
      // `notFound()` in Next.js werfen NEXT_REDIRECT / NEXT_HTTP_ERROR_FALLBACK
      // als Control-Flow – keine echten Fehler. `before_send` dropt sie clientseitig.
      before_send: (event) => {
        if (event?.event === '$exception') {
          const values =
            (event.properties?.$exception_list as
              | Array<{ type?: string; value?: string }>
              | undefined) ?? [];
          const isFrameworkControlFlow = values.some((v) => {
            const t = `${v?.type ?? ''} ${v?.value ?? ''}`;
            return (
              t.includes('NEXT_REDIRECT') ||
              t.includes('NEXT_HTTP_ERROR_FALLBACK') ||
              t.includes('NEXT_NOT_FOUND')
            );
          });
          if (isFrameworkControlFlow) return null;
        }

        // Die Messung meldet nicht sich selbst. Ausnahmen aus dem Sendeweg von
        // posthog-js sind Transportrauschen und wurden in #1086 bereits einmal
        // als Produktfehler fehlgedeutet — Begruendung und die enge Bedingung
        // stehen in `posthog-eigenrauschen.ts`.
        if (istEigenesMessrauschen(event)) return null;

        // #479 Belt-and-braces: Recording-Snapshots von Bewerber-Pfaden NIE
        // senden (primärer Schutz ist stopSessionRecording im Gate – dieser
        // Drop fängt Race-Reste beim Pfadwechsel ab).
        if (
          event?.event === '$snapshot' &&
          typeof window !== 'undefined' &&
          isApplicantPath(window.location.pathname)
        ) {
          return null;
        }

        // #479: Token-/ID-Segmente in URL-Properties maskieren (alle Events).
        return sanitizeEventUrls(event);
      },
      loaded: (ph) => {
        // Instanz-Setup (super-properties, First-Touch, Recording-Gate,
        // Initial-$pageview) – geteilt mit dem Re-Consent-Pfad in initialize()
        // über setupInstance (Finding B, Spec 2026-07-10).
        setupInstance(ph);
      },
    });
  },

  shutdown: async () => {
    // Bei Consent-Widerruf: Tracking stoppen + Identifier resetten.
    // posthog.reset() löscht Distinct-ID; opt_out_capturing verhindert weitere
    // Events. CookieYes Auto-Blocker entfernt die Cookies physisch.
    if (typeof window === 'undefined') return;
    posthog.opt_out_capturing();
    posthog.reset();
  },

  trackPageView: async () => {
    // #576: capture_pageview ist aus → SPA-Navigations-$pageviews kommen HIER an
    // (useReportPageView feuert bei jedem URL-Change). Das Initial-$pageview macht
    // der loaded-Callback; dieser Pfad ist erst nach Consent aktiv (Manager routet
    // vor enableServices in den NullAnalyticsService) → kein Doppel-Initial.
    if (typeof window === 'undefined') return;
    posthog.capture('$pageview');
  },

  trackEvent: async (
    eventName: string,
    eventProperties?: Record<string, string | string[]>,
  ) => {
    if (typeof window !== 'undefined') {
      posthog.capture(eventName, eventProperties);
    }
  },

  identify: async (userId: string, traits?: Record<string, string>) => {
    if (typeof window !== 'undefined') {
      posthog.identify(userId, traits);
    }
  },
};
